import React, { useState, useEffect } from 'react';
import { 
    Star, Plus, Edit, Trash2, Award, Users, Search, 
    Filter, Save, X, Check, AlertCircle, Eye, Settings
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const BadgeManagement = () => {
    const { user } = useAuth();
    const [badges, setBadges] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedBadge, setSelectedBadge] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [loadingUsers, setLoadingUsers] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        icon: '',
        color_scheme: 'purple',
        category: 'general',
        auto_award: false,
        auto_award_condition: '',
        is_active: true
    });

    // Fetch badges and users
    useEffect(() => {
        fetchBadges();
        fetchUsers();
    }, []);

    // Reload users when selected badge changes in assign modal
    useEffect(() => {
        if (showAssignModal && selectedBadge) {
            fetchUsers(selectedBadge.id);
        }
    }, [selectedBadge, showAssignModal]);

    const fetchBadges = async () => {
        try {
            const response = await api.get('/badges');
            setBadges(response.data.data || []);
        } catch (error) {
            console.error('Error fetching badges:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async (excludeBadgeId = null) => {
        try {
            setLoadingUsers(true);
            console.log('Fetching users for badge management...', excludeBadgeId ? `(excluding badge ${excludeBadgeId})` : '');
            
            const response = await api.get('/users');
            console.log('Users API response:', response.data);
            
            if (response.data.success && response.data.data) {
                let filteredUsers = response.data.data;
                
                // If a badge is selected, filter out users who already have it
                if (excludeBadgeId) {
                    try {
                        const usersWithBadgeResponse = await api.get(`/badges/${excludeBadgeId}/users`);
                        if (usersWithBadgeResponse.data.success) {
                            const userIdsWithBadge = usersWithBadgeResponse.data.data.map(user => user.id);
                            filteredUsers = response.data.data.filter(user => !userIdsWithBadge.includes(user.id));
                            console.log(`Filtered out ${userIdsWithBadge.length} users who already have badge ${excludeBadgeId}`);
                            console.log(`${filteredUsers.length} users eligible for this badge`);
                        }
                    } catch (badgeError) {
                        console.warn('Could not fetch users with badge:', badgeError);
                        // Continue with all users if filtering fails
                    }
                }
                
                setUsers(filteredUsers);
                console.log('Successfully loaded', filteredUsers.length, 'users');
            } else {
                console.error('Invalid users response format:', response.data);
                setUsers([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleCreateBadge = async (e) => {
        e.preventDefault();
        try {
            await api.post('/badges', formData);
            setShowCreateModal(false);
            resetForm();
            fetchBadges();
        } catch (error) {
            console.error('Error creating badge:', error);
        }
    };

    const handleUpdateBadge = async (e) => {
        e.preventDefault();
        try {
            await api.put(`/badges/${selectedBadge.id}`, formData);
            setShowEditModal(false);
            resetForm();
            fetchBadges();
        } catch (error) {
            console.error('Error updating badge:', error);
        }
    };

    const handleDeleteBadge = async (badgeId) => {
        if (window.confirm('Are you sure you want to delete this badge? This will remove it from all users.')) {
            try {
                await api.delete(`/badges/${badgeId}`);
                fetchBadges();
            } catch (error) {
                console.error('Error deleting badge:', error);
            }
        }
    };

    const handleAwardBadge = async (userId, badgeId) => {
        try {
            await api.post('/badges/award', { userId, badgeId });
            setShowAssignModal(false);
            alert('Badge awarded successfully!');
        } catch (error) {
            console.error('Error awarding badge:', error);
            alert('Error awarding badge. User may already have this badge.');
        }
    };

    const handleRemoveBadge = async (userId, badgeId) => {
        if (window.confirm('Are you sure you want to remove this badge from the user?')) {
            try {
                await api.delete('/badges/remove', { data: { userId, badgeId } });
                alert('Badge removed successfully!');
            } catch (error) {
                console.error('Error removing badge:', error);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            icon: '',
            color_scheme: 'purple',
            category: 'general',
            auto_award: false,
            auto_award_condition: '',
            is_active: true
        });
    };

    const openEditModal = (badge) => {
        setSelectedBadge(badge);
        setFormData({
            name: badge.name,
            description: badge.description,
            icon: badge.icon,
            color_scheme: badge.color_scheme,
            category: badge.category,
            auto_award: badge.auto_award,
            auto_award_condition: badge.auto_award_condition || '',
            is_active: badge.is_active
        });
        setShowEditModal(true);
    };

    const filteredBadges = badges.filter(badge => {
        const matchesSearch = badge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            badge.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'all' || badge.category === filterCategory;
        return matchesSearch && matchesCategory;
    });

    const colorSchemes = {
        purple: 'from-purple-600/20 to-blue-600/20 border-purple-500/30',
        blue: 'from-blue-600/20 to-cyan-600/20 border-blue-500/30',
        green: 'from-green-600/20 to-emerald-600/20 border-green-500/30',
        yellow: 'from-yellow-600/20 to-orange-600/20 border-yellow-500/30',
        pink: 'from-pink-600/20 to-rose-600/20 border-pink-500/30',
        gold: 'from-yellow-500/20 to-amber-600/20 border-yellow-400/30'
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center py-12">
                        <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                        <p className="text-gray-400">Loading badge management...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-black gradient-text mb-4 flex items-center gap-3">
                        <Star className="w-10 h-10 text-yellow-400" />
                        Badge Management
                    </h1>
                    <p className="text-gray-400">
                        Create, manage, and assign badges to users in The Deep Wave community.
                    </p>
                </div>

                {/* Controls */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-4 flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search badges..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Categories</option>
                            <option value="general">General</option>
                            <option value="content">Content</option>
                            <option value="social">Social</option>
                            <option value="events">Events</option>
                            <option value="special">Special</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowAssignModal(true)}
                            className="btn-secondary flex items-center gap-2"
                        >
                            <Award className="w-4 h-4" />
                            Assign Badge
                        </button>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="btn-primary flex items-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            Create Badge
                        </button>
                    </div>
                </div>

                {/* Badges Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredBadges.map(badge => (
                        <div key={badge.id} className={`glass-card p-6 bg-gradient-to-br ${colorSchemes[badge.color_scheme]} border`}>
                            <div className="flex items-start justify-between mb-4">
                                <div className="text-center flex-1">
                                    <div className="text-4xl mb-2">{
                                        // Map icon names to emojis
                                        (() => {
                                            const iconMap = {
                                                wave: '🌊',
                                                edit: '📝',
                                                calendar: '🎪',
                                                users: '👥',
                                                music: '🎵',
                                                verified: '✅',
                                                headphones: '🎧',
                                                heart: '🦋',
                                                mic: '🎤',
                                                star: '🏆',
                                                crown: '👑'
                                            };
                                            return iconMap[badge.icon] || '🏅';
                                        })()
                                    }</div>
                                    <h3 className="text-lg font-bold text-white mb-1">{badge.name}</h3>
                                    <p className="text-sm text-gray-300 mb-2">{badge.description}</p>
                                    <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
                                        <span className="flex items-center gap-1">
                                            <Users className="w-3 h-3" />
                                            {badge.awarded_count || 0}
                                        </span>
                                        <span className={`px-2 py-1 rounded-full ${badge.is_active ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                                            {badge.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    {badge.auto_award && (
                                        <div className="mt-2">
                                            <span className="inline-block px-2 py-1 bg-blue-600/20 text-blue-400 text-xs rounded-full">
                                                Auto-Award: {badge.auto_award_condition}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => openEditModal(badge)}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                                >
                                    <Edit className="w-4 h-4" />
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDeleteBadge(badge.id)}
                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg flex items-center justify-center transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Create Badge Modal */}
                {showCreateModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="glass-card max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                                <Plus className="w-6 h-6 text-blue-400" />
                                Create New Badge
                            </h2>
                            
                            <form onSubmit={handleCreateBadge} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                                    <textarea
                                        required
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        rows={3}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Icon (Emoji)</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="🌊"
                                        value={formData.icon}
                                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Color Scheme</label>
                                        <select
                                            value={formData.color_scheme}
                                            onChange={(e) => setFormData({ ...formData, color_scheme: e.target.value })}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="purple">Purple</option>
                                            <option value="blue">Blue</option>
                                            <option value="green">Green</option>
                                            <option value="yellow">Yellow</option>
                                            <option value="pink">Pink</option>
                                            <option value="gold">Gold</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                                        <select
                                            value={formData.category}
                                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="general">General</option>
                                            <option value="content">Content</option>
                                            <option value="social">Social</option>
                                            <option value="events">Events</option>
                                            <option value="special">Special</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="auto_award"
                                        checked={formData.auto_award}
                                        onChange={(e) => setFormData({ ...formData, auto_award: e.target.checked })}
                                        className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                                    />
                                    <label htmlFor="auto_award" className="text-sm text-gray-300">
                                        Auto-award this badge
                                    </label>
                                </div>

                                {formData.auto_award && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Auto-award Condition</label>
                                        <select
                                            value={formData.auto_award_condition}
                                            onChange={(e) => setFormData({ ...formData, auto_award_condition: e.target.value })}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Select condition</option>
                                            <option value="signup">On signup</option>
                                            <option value="first_post">First post</option>
                                            <option value="artist_status">Enable artist status</option>
                                            <option value="30_days_active">30 days active</option>
                                            <option value="100_plays">100 plays</option>
                                            <option value="10_followers">10 followers</option>
                                        </select>
                                    </div>
                                )}

                                <div className="flex items-center gap-3 pt-6 border-t border-gray-700">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreateModal(false);
                                            resetForm();
                                        }}
                                        className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2"
                                    >
                                        <X className="w-4 h-4" />
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 btn-primary flex items-center justify-center gap-2"
                                    >
                                        <Save className="w-4 h-4" />
                                        Create Badge
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Edit Badge Modal */}
                {showEditModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="glass-card max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                                <Edit className="w-6 h-6 text-blue-400" />
                                Edit Badge
                            </h2>
                            
                            <form onSubmit={handleUpdateBadge} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                                    <textarea
                                        required
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        rows={3}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Icon (Emoji)</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.icon}
                                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Color Scheme</label>
                                        <select
                                            value={formData.color_scheme}
                                            onChange={(e) => setFormData({ ...formData, color_scheme: e.target.value })}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="purple">Purple</option>
                                            <option value="blue">Blue</option>
                                            <option value="green">Green</option>
                                            <option value="yellow">Yellow</option>
                                            <option value="pink">Pink</option>
                                            <option value="gold">Gold</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                                        <select
                                            value={formData.category}
                                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="general">General</option>
                                            <option value="content">Content</option>
                                            <option value="social">Social</option>
                                            <option value="events">Events</option>
                                            <option value="special">Special</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="edit_auto_award"
                                        checked={formData.auto_award}
                                        onChange={(e) => setFormData({ ...formData, auto_award: e.target.checked })}
                                        className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                                    />
                                    <label htmlFor="edit_auto_award" className="text-sm text-gray-300">
                                        Auto-award this badge
                                    </label>
                                </div>

                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="edit_is_active"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                                    />
                                    <label htmlFor="edit_is_active" className="text-sm text-gray-300">
                                        Badge is active
                                    </label>
                                </div>

                                {formData.auto_award && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Auto-award Condition</label>
                                        <select
                                            value={formData.auto_award_condition}
                                            onChange={(e) => setFormData({ ...formData, auto_award_condition: e.target.value })}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Select condition</option>
                                            <option value="signup">On signup</option>
                                            <option value="first_post">First post</option>
                                            <option value="artist_status">Enable artist status</option>
                                            <option value="30_days_active">30 days active</option>
                                            <option value="100_plays">100 plays</option>
                                            <option value="10_followers">10 followers</option>
                                        </select>
                                    </div>
                                )}

                                <div className="flex items-center gap-3 pt-6 border-t border-gray-700">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowEditModal(false);
                                            resetForm();
                                        }}
                                        className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2"
                                    >
                                        <X className="w-4 h-4" />
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 btn-primary flex items-center justify-center gap-2"
                                    >
                                        <Save className="w-4 h-4" />
                                        Save Changes
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Assign Badge Modal */}
                {showAssignModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="glass-card max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                                <Award className="w-6 h-6 text-yellow-400" />
                                Assign Badge to User
                            </h2>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Select Badge */}
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-4">Select Badge</h3>
                                    <div className="space-y-3 max-h-60 overflow-y-auto">
                                        {badges.filter(b => b.is_active).map(badge => (
                                            <div
                                                key={badge.id}
                                                onClick={() => {
                                                    setSelectedBadge(badge);
                                                    setSelectedUser(null); // Reset selected user when badge changes
                                                }}
                                                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                                    selectedBadge?.id === badge.id
                                                        ? 'border-blue-500 bg-blue-600/20'
                                                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="text-2xl">{
                                                        // Map icon names to emojis
                                                        (() => {
                                                            const iconMap = {
                                                                wave: '🌊',
                                                                edit: '📝',
                                                                calendar: '🎪',
                                                                users: '👥',
                                                                music: '🎵',
                                                                verified: '✅',
                                                                headphones: '🎧',
                                                                heart: '🦋',
                                                                mic: '🎤',
                                                                star: '🏆',
                                                                crown: '👑'
                                                            };
                                                            return iconMap[badge.icon] || '🏅';
                                                        })()
                                                    }</div>
                                                    <div>
                                                        <div className="font-semibold text-white">{badge.name}</div>
                                                        <div className="text-sm text-gray-400">{badge.description}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Select User */}
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-4">
                                        Select User
                                        {selectedBadge && (
                                            <span className="text-sm text-gray-400 font-normal ml-2">
                                                (excluding users who already have "{selectedBadge.name}")
                                            </span>
                                        )}
                                    </h3>
                                    <div className="space-y-3 max-h-60 overflow-y-auto">
                                        {loadingUsers ? (
                                            <div className="text-center text-gray-400 py-8">
                                                <div className="loading-spinner w-5 h-5 mx-auto mb-2"></div>
                                                <div>Loading eligible users...</div>
                                            </div>
                                        ) : users.length > 0 ? users.map(user => (
                                            <div
                                                key={user.id}
                                                onClick={() => setSelectedUser(user)}
                                                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                                    selectedUser?.id === user.id
                                                        ? 'border-blue-500 bg-blue-600/20'
                                                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                                                }`}
                                            >
                                                <div>
                                                    <div className="font-semibold text-white">
                                                        {user.display_name || user.username || `User #${user.id}`}
                                                    </div>
                                                    <div className="text-sm text-gray-400">{user.email || 'No email'}</div>
                                                    {user.is_artist && (
                                                        <div className="text-xs text-blue-400 mt-1">Artist</div>
                                                    )}
                                                    {user.is_admin && (
                                                        <div className="text-xs text-yellow-400 mt-1">Admin</div>
                                                    )}
                                                </div>
                                            </div>
                                        )) : (
                                            <div className="text-center text-gray-400 py-4">
                                                {selectedBadge ? 
                                                    'All users already have this badge!' : 
                                                    'No users found'
                                                }
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 pt-6 border-t border-gray-700 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAssignModal(false);
                                        setSelectedBadge(null);
                                        setSelectedUser(null);
                                    }}
                                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2"
                                >
                                    <X className="w-4 h-4" />
                                    Cancel
                                </button>
                                <button
                                    onClick={() => selectedBadge && selectedUser && handleAwardBadge(selectedUser.id, selectedBadge.id)}
                                    disabled={!selectedBadge || !selectedUser}
                                    className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Award className="w-4 h-4" />
                                    Award Badge
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BadgeManagement;